/**
 * StyleFieldRow - Reusable row for style settings
 *
 * Supports all field types:
 * - text: StyledTextField
 * - number: StyledNumberInput
 * - color: ColorInput (color picker + hex text)
 * - select: Select dropdown
 * - numberCombo: Autocomplete with freeSolo (CRITICAL - this was missing in PageIndicatorElement)
 *
 * Layout for nullable fields (isNullable !== false):
 * [✓] label
 * [Input___________]
 *
 * Layout for non-nullable fields (isNullable: false):
 * label
 * [Input___________]
 *
 * Extracted from TextElement.tsx for reuse across all editor elements.
 */

import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Checkbox,
  Typography,
  MenuItem,
  Select,
  FormControl,
  Tooltip,
  TextField,
  Autocomplete,
  Switch,
  Slider,
} from '@mui/material';
import { StyledNumberInput, StyledTextField, WithGuideHelp } from '@modules/ui-components';
import { ColorInput } from './ColorInput';
import type { FieldConfig } from '../../../types/editorFieldTypes';
import { useEditorGuideService } from '../../../contexts';
import { useFontLabels } from '@modules/font-config/context/FontLabelsContext';
import {
  fieldInputSx,
  fieldSelectSx,
  fieldMenuItemSx,
  fieldLabelSx,
  checkboxIconSx,
  checkboxCheckedIconSx,
  checkboxSx,
  SHOW_FIELD_PATH_TOOLTIP,
  STYLE_FIELD_HEIGHT,
  STYLE_FIELD_LABEL_HEIGHT,
} from '../../../config/editorFieldStyles';
import { FIELD_GAP_SMALL } from '../../../config/ui';

/**
 * Props for StyleFieldRow component
 */
export interface StyleFieldRowProps {
  /** Field configuration */
  config: FieldConfig;
  /** Current field value */
  value: unknown;
  /** Callback when value changes */
  onChange: (value: unknown) => void;
  /** Whether field is disabled */
  disabled?: boolean;
  /** Current locale — required for imageUpload fields (LocalizedImage URL extraction) */
  locale?: string;
  /** Test ID for input element */
  inputTestId?: string;
  /** Test ID for checkbox element (nullable fields only) */
  checkboxTestId?: string;
}

/**
 * StyleFieldRow - Universal row for style settings
 *
 * For 2-column grid - each row is a single grid cell
 */
export const StyleFieldRow: React.FC<StyleFieldRowProps> = ({
  config,
  value,
  onChange,
  disabled = false,
  locale,
  inputTestId,
  checkboxTestId,
}) => {
  // Check if field is nullable (default: true for backward compatibility)
  const isNullable = config.isNullable !== false;

  // Get guideService from context for WithGuideHelp
  const guideService = useEditorGuideService();

  // Font labels from project font config (e.g., "SF Pro" → "Main Font")
  const fontLabels = useFontLabels();
  const isFontFamilyField = config.path.endsWith('fontFamily');
  const shouldCoerceSelectToNumber = config.numericValue === true || config.path.endsWith('fontWeight');
  const shouldCoerceSelectToBoolean = config.booleanValue === true;

  const coerceSelectValue = useCallback(
    (raw: unknown): unknown => {
      if (shouldCoerceSelectToBoolean) {
        if (raw === 'true') return true;
        if (raw === 'false') return false;
      }
      if (!shouldCoerceSelectToNumber) return raw;
      const numeric = Number(raw);
      return Number.isNaN(numeric) ? raw : numeric;
    },
    [shouldCoerceSelectToBoolean, shouldCoerceSelectToNumber]
  );

  // For nullable fields, track enabled state; non-nullable are always enabled
  const [internalEnabled, setInternalEnabled] = useState(
    isNullable ? (value !== null && value !== undefined) : true
  );

  // SYNC FIX: Keep internalEnabled in sync with external value prop changes.
  // This handles scenarios where the parent updates the value prop externally
  // (e.g., from undo/redo, other field interactions, or race conditions during batched updates).
  // Without this, the checkbox state could become out of sync with the actual value.
  useEffect(() => {
    if (isNullable) {
      const shouldBeEnabled = value !== null && value !== undefined;
      if (internalEnabled !== shouldBeEnabled) {
        setInternalEnabled(shouldBeEnabled);
      }
    }
  }, [value, isNullable]); // Note: intentionally NOT including internalEnabled to avoid infinite loop

  // Helper to get type-appropriate default value
  const getDefaultForType = useCallback(
    (type: string): unknown => {
      switch (type) {
        case 'number':
        case 'numberCombo':
        case 'slider':
          return 0;
        case 'color':
          return '#000000';
        case 'select':
          return config.options?.[0]?.value ?? '';
        case 'text':
        default:
          return '';
      }
    },
    [config.options]
  );

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!isNullable) return; // Guard for non-nullable fields
      const checked = event.target.checked;
      setInternalEnabled(checked);
      if (!checked) {
        // DISABLE: Send null to delete value from struct
        onChange(null);
      } else {
        // ENABLE: Send default value to save it in struct
        const defaultVal = config.defaultValue ?? getDefaultForType(config.type);
        onChange(coerceSelectValue(defaultVal));
      }
    },
    [onChange, isNullable, config.defaultValue, config.type, getDefaultForType, coerceSelectValue]
  );

  const renderInput = () => {
    // Non-nullable fields are always enabled; nullable respect checkbox state
    const isDisabled = disabled || (isNullable && !internalEnabled);

    // Stable per-field test id: callers may pass an explicit inputTestId, but when
    // they don't we derive one from the field's config path (the natural, unique id
    // — same value the resolver/catalog key on). This makes every settings input
    // findable/clippable by `[data-testid="input-<path>"]` with no per-call-site
    // wiring. Distinct from the label's own `data-testid={config.path}` (no collision).
    const resolvedInputTestId = inputTestId ?? `input-${config.path}`;

    switch (config.type) {
      case 'number':
        return (
          <StyledNumberInput
            value={typeof value === 'number' ? value : 0}
            onChange={(newValue: number | null) => onChange(newValue)}
            disabled={isDisabled}
            size="small"
            variant="standard"
            min={config.min}
            max={config.max}
            step={config.step}
            data-testid={resolvedInputTestId}
            sx={{
              ...fieldInputSx,
              '& .MuiInputBase-input': { textAlign: 'left' },
            }}
          />
        );

      case 'color':
        // Color picker + hex text input with validation
        return (
          <ColorInput
            value={typeof value === 'string' ? value : '#ffffff'}
            onChange={onChange}
            disabled={isDisabled}
            testId={resolvedInputTestId}
          />
        );

      case 'select':
        return (
          <FormControl size="small" fullWidth disabled={isDisabled} variant="standard">
            <Select
              value={
                typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean'
                  ? String(value)
                  : config.defaultValue !== null && config.defaultValue !== undefined
                    ? String(config.defaultValue)
                    : ''
              }
              onChange={(e) => {
                const raw = e.target.value;
                onChange(coerceSelectValue(raw));
              }}
              displayEmpty
              sx={fieldSelectSx}
              data-testid={inputTestId || `${config.path}-select`}
              // NOTE: select keeps its historical `${path}-select` fallback (referenced by existing recipes)
              {...(isFontFamilyField && Object.keys(fontLabels).length > 0 && {
                renderValue: (selected: unknown) => {
                  const s = String(selected);
                  return fontLabels[s] || config.options?.find(o => o.value === s)?.label || s;
                },
              })}
            >
              {config.options?.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={fieldMenuItemSx}>
                  {isFontFamilyField && fontLabels[opt.value] ? fontLabels[opt.value] : opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      // CRITICAL: This case was missing in PageIndicatorElement!
      case 'numberCombo': {
        const scrollToValue = value != null ? String(value) : String(config.defaultScrollValue ?? 18);
        return (
          <Autocomplete
            freeSolo
            disableClearable
            selectOnFocus
            clearOnBlur={false}  // CRITICAL FIX: Disable to prevent value loss on blur
            handleHomeEndKeys
            size="small"
            disabled={isDisabled}
            options={config.options?.map((o) => o.value) || []}
            value={value != null ? String(value) : ''}
            onChange={(_, newValue) => {
              if (newValue != null) {
                const num = parseFloat(newValue);
                if (!isNaN(num)) onChange(num);
              }
            }}
            onInputChange={(_, newValue, reason) => {
              if (reason === 'input' && newValue) {
                const num = parseFloat(newValue);
                if (!isNaN(num)) onChange(num);
              }
            }}
            onOpen={() => {
              setTimeout(() => {
                const listbox = document.querySelector('.MuiAutocomplete-listbox');
                const options = listbox?.querySelectorAll('[role="option"]');
                const targetOption = Array.from(options || []).find(
                  (opt) => opt.textContent === scrollToValue
                );
                targetOption?.scrollIntoView({ block: 'center' });
              }, 0);
            }}
            ListboxProps={{ sx: { maxHeight: 200 } }}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="standard"
                sx={fieldInputSx}
                slotProps={{
                  input: {
                    ...params.InputProps,  // Preserve Autocomplete's InputProps (adornments, etc.)
                  },
                  htmlInput: {
                    ...params.inputProps,  // Preserve Autocomplete's inputProps (refs, handlers)
                    'data-testid': resolvedInputTestId,  // Add our test ID
                  },
                }}
              />
            )}
            sx={{ width: '100%' }}
          />
        );
      }

      case 'toggle':
        return (
          <Switch
            checked={!!value}
            onChange={(e) => onChange(e.target.checked)}
            disabled={isDisabled}
            size="small"
            data-testid={resolvedInputTestId}
          />
        );

      case 'slider': {
        const numericValue = typeof value === 'number' ? value : Number(config.defaultValue ?? config.min ?? 0);
        const min = config.min ?? 0;
        const max = config.max ?? 100;
        const step = config.step ?? 1;
        const handleNumberChange = (newValue: number | null) => {
          const nextValue = typeof newValue === 'number' ? newValue : min;
          onChange(Math.max(min, Math.min(max, nextValue)));
        };
        const handleSliderChange = (_event: Event, newValue: number | number[]) => {
          if (typeof newValue === 'number') {
            onChange(newValue);
          }
        };

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
            <StyledNumberInput
              value={numericValue}
              onChange={handleNumberChange}
              disabled={isDisabled}
              size="small"
              variant="standard"
              min={min}
              max={max}
              step={step}
              data-testid={resolvedInputTestId}
              sx={{
                ...fieldInputSx,
                width: 64,
                minWidth: 64,
                '& .MuiInputBase-input': { textAlign: 'left' },
              }}
            />
            <Slider
              value={numericValue}
              onChange={handleSliderChange}
              disabled={isDisabled}
              min={min}
              max={max}
              step={step}
              aria-label={config.label}
              sx={{
                flex: 1,
                '& .MuiSlider-thumb': { width: 14, height: 14 },
                '& .MuiSlider-track': { height: 4 },
                '& .MuiSlider-rail': { height: 4 },
              }}
            />
          </Box>
        );
      }

      case 'imageUpload': {
        // Extract URL from LocalizedImage: { translations: { [locale]: AssetReference } }
        // V2 AssetReference = { original?: string, small?: string } (NOT { assetUrl })
        const extractUrl = (): string => {
          if (!value || typeof value !== 'object') return '';
          const translations = (value as Record<string, unknown>).translations as
            Record<string, Record<string, string>> | undefined;
          if (!translations || !locale) return '';
          const assetRef = translations[locale];
          if (!assetRef) return '';
          return assetRef.original ?? assetRef.small ?? '';
        };
        return (
          <StyledTextField
            value={extractUrl()}
            onChange={(newUrl: string) => {
              if (!newUrl) { onChange(null); return; }
              if (locale) {
                // Store as V2 AssetReference: { original, small } — both point to same URL
                onChange({ translations: { [locale]: { original: newUrl, small: newUrl } } });
              }
            }}
            disabled={isDisabled}
            size="small"
            variant="standard"
            fullWidth
            placeholder="Image URL"
            data-testid={resolvedInputTestId}
            sx={fieldInputSx}
          />
        );
      }

      case 'text':
      default:
        return (
          <StyledTextField
            value={typeof value === 'string' ? value : ''}
            onChange={(newValue: string) => onChange(newValue)}
            disabled={isDisabled}
            size="small"
            variant="standard"
            fullWidth
            data-testid={resolvedInputTestId}
            sx={fieldInputSx}
          />
        );
    }
  };

  return (
    <Box
      // Uniform per-field clip target for guide-screenshot capture: the whole
      // label+input row is addressable by `field-<config.path>`, regardless of
      // whether an explicit inputTestId was passed. Path is unique per screen.
      data-testid={`field-${config.path}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: FIELD_GAP_SMALL,
      }}
    >
      {/* Label row: optional checkbox (for nullable) + label */}
      {/* Fixed height ensures grid alignment across all field types */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: FIELD_GAP_SMALL, height: STYLE_FIELD_LABEL_HEIGHT, minHeight: STYLE_FIELD_LABEL_HEIGHT }}>
        {isNullable && (
          <Checkbox
            checked={internalEnabled}
            onChange={handleCheckboxChange}
            disabled={disabled}
            size="small"
            icon={<Box sx={checkboxIconSx} />}
            checkedIcon={<Box sx={checkboxCheckedIconSx}>✓</Box>}
            sx={checkboxSx}
            data-testid={checkboxTestId}
            inputProps={{
              'aria-label': config.label,
              'data-testid': checkboxTestId ? `${checkboxTestId}-input` : undefined,
            } as React.InputHTMLAttributes<HTMLInputElement>}
          />
        )}
        {/* Label with optional guide help */}
        {config.guideKey && guideService ? (
          <WithGuideHelp
            guideKey={config.guideKey}
            guideService={guideService}
            data-testid={`guide-anchor-${config.guideKey}`}
            position="inline-end"
            iconSize="small"
          >
            <Tooltip
              title={SHOW_FIELD_PATH_TOOLTIP ? config.path : ''}
              placement="top"
              arrow
              disableHoverListener={!SHOW_FIELD_PATH_TOOLTIP}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={fieldLabelSx}
                data-testid={config.path}
                aria-label={config.path}
              >
                {config.label}
              </Typography>
            </Tooltip>
          </WithGuideHelp>
        ) : (
          <Tooltip
            title={SHOW_FIELD_PATH_TOOLTIP ? config.path : ''}
            placement="top"
            arrow
            disableHoverListener={!SHOW_FIELD_PATH_TOOLTIP}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={fieldLabelSx}
              data-testid={config.path}
              aria-label={config.path}
            >
              {config.label}
            </Typography>
          </Tooltip>
        )}
      </Box>
      {/* Input row: full width */}
      {/* Fixed height ensures grid alignment across all input types */}
      <Box sx={{ height: STYLE_FIELD_HEIGHT, minHeight: STYLE_FIELD_HEIGHT, display: 'flex', alignItems: 'center' }}>{renderInput()}</Box>
    </Box>
  );
};

export default StyleFieldRow;
